import React from 'react';

import ContentCard, { TrackingSource } from '../content-card';
import CardImage from '../card-image';
import ProductImages from '../../toplists/components/product-images';

import { compareByDate } from '../../utils';

import { ContentTypes } from '../../types';
import {
  AdvizedTest,
  ToplistHeadline,
  AdvizedToplist,
  ExpertGuide,
  AdvizedCategory,
} from '../../../../types';

type Props = {
  className?: string;
  items: Items;
  imageSize?: number;
  limit?: number;
  offset?: number;
  hideExperts?: boolean;
  category?: AdvizedCategory;
  trackingSource?: TrackingSource;
};

type Items = (
  | AdvizedToplist
  | ToplistHeadline
  | AdvizedTest
  | ExpertGuide
  | null
)[];

export default function ContentCards({
  category,
  className,
  items,
  imageSize = 130,
  limit = 0,
  offset = 0,
  hideExperts,
  trackingSource,
}: Props) {
  const numberOfCards = limit ? limit + offset : items.length;
  return (
    <>
      {items.sort(compareByDate).map((item, index) => {
        if (!item || !item.id || !item.__typename) {
          return null;
        }

        if (index < offset || index >= numberOfCards) {
          return null;
        }

        switch (item.__typename) {
          case ContentTypes.TOPLIST_HEADLINE: {
            if (!item.toplists || !item.toplists[0]) {
              return null;
            }
            return (
              <ContentCard
                trackingSource={trackingSource}
                category={category}
                key={item.id}
                className={className}
                type={ContentTypes.TOPLIST_HEADLINE}
                title={item.name}
                url={item.url}
                likes={item.toplists[0].headlineInfo?.likes}
                date={item.toplists[0].date}
                experts={
                  !hideExperts && item.toplists[0].expert.url
                    ? [
                        item.toplists[0].expert,
                        ...(item.toplists[0]?.headlineInfo?.otherExperts || []),
                      ]
                    : undefined
                }
                media={
                  <ProductImages
                    height={`${imageSize}px`}
                    background={false}
                    selectedPosition={2}
                    products={item.toplists[0]?.products?.map(p => ({
                      ...p,
                    }))}
                  />
                }
              />
            );
          }

          case ContentTypes.TOPLIST: {
            return (
              <ContentCard
                trackingSource={trackingSource}
                category={category}
                key={item.id}
                className={className}
                type={ContentTypes.TOPLIST}
                url={item.headlineInfo.url}
                title={item.title}
                date={item.date}
                likes={item.headlineInfo?.likes}
                experts={
                  !hideExperts && item.expert?.url
                    ? [item.expert, ...(item.headlineInfo?.otherExperts || [])]
                    : undefined
                }
                media={
                  <ProductImages
                    height={`${imageSize}px`}
                    products={item.products.map(p => ({ ...p }))}
                    background={false}
                  />
                }
              />
            );
          }

          case ContentTypes.TEST: {
            return (
              <ContentCard
                trackingSource={trackingSource}
                category={category}
                key={item.id}
                className={className}
                type={ContentTypes.TEST}
                url={item.url}
                title={item.title}
                date={item.date}
                likes={item.likes}
                grade={item.grade}
                experts={!hideExperts ? [item.expert] : undefined}
                media={
                  item.imageWithSize && (
                    <CardImage
                      src={item.imageWithSize.src}
                      alt={item.imageWithSize.alt}
                      height={imageSize}
                    />
                  )
                }
              />
            );
          }

          case ContentTypes.GUIDE: {
            return (
              <ContentCard
                trackingSource={trackingSource}
                category={category}
                key={item.id}
                className={className}
                type={ContentTypes.GUIDE}
                title={item.title}
                url={item.url}
                experts={!hideExperts ? [item.expert] : undefined}
                date={item.date}
                likes={item.likes}
                media={
                  item.image && (
                    <CardImage
                      src={item.image.src}
                      alt={item.image.alt}
                      height={imageSize}
                    />
                  )
                }
              />
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
}
